// The persistent chrome around every authed screen — top bar with clinic
// name + clinician + sign-out, plus a role-gated primary nav. Which tabs
// render is driven by getRolePermissions(member.role); see lib/permissions.ts
// for the map.

import type { ReactNode } from 'react';
import type { Clinic, ClinicMember } from '@pamosi/shared';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { NetworkBanner } from '../components/NetworkBanner';
import { Logo } from '../components/Logo';
import { InboxBadge } from '../components/InboxBadge';
import { getRolePermissions, type ClinicTab } from '../lib/permissions';

const TAB_LABELS: Record<ClinicTab, string> = {
    reception: 'Reception',
    queues: 'Queues',
    'work-list': 'Work list',
    pharmacy: 'Pharmacy',
    payments: 'Payments',
    'child-health': 'Child health',
    procedures: 'Procedures',
    labs: 'Labs',
    team: 'Team',
    'patient-records': 'Patient records',
    'shift-reports': 'Shift reports',
    activity: 'Activity',
};

export function AppShell(
    { clinic, member, children }:
    { clinic: Clinic; member: ClinicMember | null; children: ReactNode },
) {
    const { clinician, signOut } = useAuth();
    // No member should be impossible at the AppShell layer (ClinicGate short-
    // circuits earlier). Fall back to the clinical permission shape if it
    // does happen so nothing crashes — the empty nav is a fine failure mode.
    const perms = member ? getRolePermissions(member.role) : { tabs: [] as ClinicTab[], landing: '/work-list' };
    const visibleTabs = perms.tabs;
    return (
        <div className="min-h-full flex flex-col">
            <NetworkBanner />

            <header className="bg-white border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={32} />
                        <div className="border-l border-slate-200 pl-3">
                            <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold">
                                Clinic
                            </div>
                            <div className="text-slate-900 font-medium">{clinic.facility_name}</div>
                        </div>
                    </div>
                    <nav className="hidden md:flex items-center gap-1">
                        {visibleTabs.map((t) => (
                            <NavItem key={t} to={`/${t}`}>{TAB_LABELS[t]}</NavItem>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        {/* Phase 2c — @mention inbox. Available for every
                            clinic role; a teammate can tag anyone. */}
                        <InboxBadge />
                        <div className="text-sm text-slate-600 hidden sm:block">
                            {clinician?.full_name ?? clinician?.phone_e164 ?? ''}
                        </div>
                        <button className="pm-btn-ghost" onClick={signOut}>Sign out</button>
                    </div>
                </div>
                <nav className="md:hidden border-t border-slate-100 px-4 py-2 flex items-center gap-1 max-w-5xl mx-auto overflow-x-auto">
                    {visibleTabs.map((t) => (
                        <NavItem key={t} to={`/${t}`}>{TAB_LABELS[t]}</NavItem>
                    ))}
                </nav>
            </header>
            <main className="flex-1">
                <div className="max-w-5xl mx-auto px-4 py-6">
                    {children}
                </div>
            </main>
        </div>
    );
}

function NavItem({ to, children }: { to: string; children: ReactNode }) {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                `px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    isActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`
            }
        >
            {children}
        </NavLink>
    );
}
